import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { ExternalIntegrationIds } from "@remar/shared/dist/constants";

import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

import ReactPixel from "react-facebook-pixel";

import { useHistory } from "react-router-dom";
import { RootState, useAppSelector } from "store";

import { changeSubscriptionType, selectInactiveSubscription, setError } from "store/features/Auth/authSlice";
import {
	renewSubscription,
	selectSubcriptionIsLoading,
	upgradeSubscription
} from "store/features/MyAccount/myAccountSlice";

import { PaymentSchema, ShippingSchema } from "modules/Auth/components/schemas";
import { PixelEvent, getCallbackUrl, setTiktokEvent } from "modules/utils";

interface InType {
	setModal: Dispatch<SetStateAction<boolean>>;
	onSubmit: () => void;
	dispatch: Dispatch<unknown>;
	isRenewSubscription?: boolean;
}

interface OutType {
	closeModal;
	handleSubmit;
	handleNext;
	handleBack;
	validationSchema;
	initialFormValues;
	subcriptionIsLoading;
	error;
	activeStep;
	setActiveStep;
	initStep?: number;
}

export const useUpgradeForm = ({ setModal, onSubmit, dispatch, isRenewSubscription }: InType): OutType => {
	const stripe = useStripe();
	const elements = useElements();
	const initStep = useMemo(() => (isRenewSubscription ? 1 : 0), [isRenewSubscription]);
	const [activeStep, setActiveStep] = useState(initStep);
	const subcriptionIsLoading = useAppSelector(selectSubcriptionIsLoading);
	const { subscriptionId } = useAppSelector(selectInactiveSubscription);
	const {
		errorMessage: error,
		validatedCoupon,
		inactiveSubscription: { nextUserSubscriptionType: activeSubscription },
		paymentDiscount
	}: RootState["auth"] = useAppSelector((store: RootState) => store.auth);
	const history = useHistory();
	const callBackUrl = useMemo(() => getCallbackUrl(history.location.search), [history.location.search]);
	const initialFormValues = {
		countryId: "",
		address1: "",
		address2: "",
		city: "",
		state: "",
		zip: "",
		phoneNumber: ""
	};

	const { userInfo } = useAppSelector((store: RootState) => store.myAccount);

	useEffect(() => {
		if (activeSubscription) {
			dispatch(changeSubscriptionType(activeSubscription?.id as number));
		}
	}, [activeSubscription, dispatch]);

	const validationSchema = useMemo(() => {
		switch (activeStep) {
			case 1:
				return ShippingSchema;
			case 2:
				return PaymentSchema;
		}
	}, [activeStep]);

	const handleNext = (values: typeof initialFormValues) => {
		if (activeStep === 1) {
			setActiveStep(2);
		} else if (activeStep === 2) {
			handleSubmit(values);
		}
	};

	const upgradeSideEffect = useCallback(
		values => {
			const priceData = activeSubscription?.subTypeEIDItems?.find(
				item => item.integrationId == ExternalIntegrationIds.Stripe
			)?.data;
			const { price } = priceData;
			ReactPixel.track(PixelEvent.Purchase, { ...values, value: price, currency: "USD" });
			setTiktokEvent(PixelEvent.CompletePayment, { ...values, value: price, currency: "USD" });
			if (callBackUrl) {
				window.location.replace(callBackUrl);
			} else {
				onSubmit();
			}
		},
		[activeSubscription?.subTypeEIDItems, callBackUrl, onSubmit]
	);

	const renewSideEffect = useCallback(() => {
		if (callBackUrl) {
			window.location.replace(callBackUrl);
		} else {
			onSubmit();
		}
	}, [callBackUrl, onSubmit]);

	const handleBack = () => {
		setActiveStep(prevActiveStep => (prevActiveStep >= 1 ? prevActiveStep - 1 : 0));
	};

	const handleSubmit = (values: typeof initialFormValues) => {
		const couponCode = validatedCoupon && validatedCoupon.coupon.code;
		const paymentAndDiscountId = paymentDiscount?.id;
		dispatch(setError(""));
		if (isRenewSubscription) {
			dispatch(
				renewSubscription({
					id: subscriptionId as number,
					subscriptionBody: {
						...values,
						zipCode: values.zip,
						userId: userInfo?.shippingDetails?.userId,
						fullName: userInfo?.shippingDetails?.fullName || userInfo!.firstName + userInfo!.lastName || ""
					},
					dispatch,
					sideEffect: renewSideEffect,
					handleError: setError,
					CardElement,
					elements,
					stripe,
					couponCode
				})
			);
		} else {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			dispatch(
				upgradeSubscription({
					CardElement,
					elements,
					stripe,
					values,
					sideEffect: upgradeSideEffect,
					couponCode,
					paymentAndDiscountId
				})
			).then(r => {
				if (r.error) {
					dispatch(setError(r.error.message));
					setActiveStep(3);
				}
			});
		}
	};

	const closeModal = () => {
		setModal(false);
		setActiveStep(isRenewSubscription ? 1 : 0);
	};

	return {
		closeModal,
		handleSubmit,
		handleNext,
		handleBack,
		validationSchema,
		initialFormValues,
		subcriptionIsLoading,
		error,
		activeStep,
		setActiveStep,
		initStep
	};
};
